import React, { useState, useEffect } from 'react';
import { X, Download, FileText, Loader } from 'lucide-react';
import Modal from './Modal';

const ExportModal = ({ isOpen, onClose, categories, onExport }) => {
  const [exportOptions, setExportOptions] = useState({
    category: '',
    status: 'all', // all, active, inactive
    stock: 'all', // all, instock, outofstock
  });
  const [exportLoading, setExportLoading] = useState({ pdf: false, csv: false });

  // Reset form to defaults when modal opens
  useEffect(() => {
    if (isOpen) {
      setExportOptions({
        category: '',
        status: 'all',
        stock: 'all',
      });
      setExportLoading({ pdf: false, csv: false });
    }
  }, [isOpen]);

  const handleExport = async (format) => {
    setExportLoading(prev => ({ ...prev, [format]: true }));
    await onExport(format, exportOptions);
    setExportLoading(prev => ({ ...prev, [format]: false }));
  };

  const handleReset = () => {
    setExportOptions({
      category: '',
      status: 'all',
      stock: 'all',
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export Products">
      <div className="space-y-6">
        {/* Filter Options */}
        <div className="space-y-4">
          <div>
            <label className="form-label">Category Filter</label>
            <select
              value={exportOptions.category}
              onChange={(e) => setExportOptions({ ...exportOptions, category: e.target.value })}
              className="form-select"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Export products from a specific category or all categories
            </p>
          </div>

          <div>
            <label className="form-label">Active Status</label>
            <select
              value={exportOptions.status}
              onChange={(e) => setExportOptions({ ...exportOptions, status: e.target.value })}
              className="form-select"
            >
              <option value="all">All Products</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Filter by product active status
            </p>
          </div>

          <div>
            <label className="form-label">Stock Status</label>
            <select
              value={exportOptions.stock}
              onChange={(e) => setExportOptions({ ...exportOptions, stock: e.target.value })}
              className="form-select"
            >
              <option value="all">All Products</option>
              <option value="instock">In Stock Only</option>
              <option value="outofstock">Out of Stock Only</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Filter by product stock availability
            </p>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Export Summary</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>
              <span className="font-medium">Category:</span>{' '}
              {exportOptions.category 
                ? categories.find(c => c._id === exportOptions.category)?.name 
                : 'All Categories'}
            </li>
            <li>
              <span className="font-medium">Status:</span>{' '}
              {exportOptions.status === 'all' ? 'All Products' : 
               exportOptions.status === 'active' ? 'Active Only' : 'Inactive Only'}
            </li>
            <li>
              <span className="font-medium">Stock:</span>{' '}
              {exportOptions.stock === 'all' ? 'All Products' : 
               exportOptions.stock === 'instock' ? 'In Stock Only' : 'Out of Stock Only'}
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <div className="flex gap-3">
            <button
              onClick={() => handleExport('pdf')}
              disabled={exportLoading.pdf || exportLoading.csv}
              className="flex-1 btn-primary inline-flex items-center justify-center"
            >
              {exportLoading.pdf ? (
                <>
                  <Loader className="h-5 w-5 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-2" />
                  Export as PDF
                </>
              )}
            </button>

            <button
              onClick={() => handleExport('csv')}
              disabled={exportLoading.pdf || exportLoading.csv}
              className="flex-1 btn-secondary inline-flex items-center justify-center"
            >
              {exportLoading.csv ? (
                <>
                  <Loader className="h-5 w-5 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <FileText className="h-5 w-5 mr-2" />
                  Export as CSV
                </>
              )}
            </button>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleReset}
              disabled={exportLoading.pdf || exportLoading.csv}
              className="flex-1 btn-ghost"
            >
              Reset Filters
            </button>
            
            <button
              onClick={onClose}
              disabled={exportLoading.pdf || exportLoading.csv}
              className="flex-1 btn-ghost"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ExportModal;
